import { Tooltip, Typography } from "@mui/material";
import { useSignal } from "@preact/signals-react";
import React from "react";
import { setupHandlers, setupLocalState } from "../../library/dataService";
import { INGAssistantLabelEditorProps } from "../../library/NGFieldExtensions";
import { debouncedHandler } from "../../library/utils";
import { DEFAULT_ASSISTANT_LABELS } from "./NGAssistantLabelEditorConstants";

export function NGAssistantLabelEditor({ config, context }: INGAssistantLabelEditorProps) {
  const local = setupLocalState(
    config,
    {
      Value: useSignal(config.Value ?? DEFAULT_ASSISTANT_LABELS),
    },
    context
  );

  const handlers = setupHandlers(config, context);

  const rowData = local.Value.value;

  // Ensure we have valid data with group property
  let actualRowData;

  if (Array.isArray(rowData) && rowData.length > 0 && rowData[0].group) {
    actualRowData = rowData;
    if (actualRowData.length < DEFAULT_ASSISTANT_LABELS.length) {
      // Add missing default labels
      const existingLabels = new Set(actualRowData.map((r) => r.label));
      const missingDefaults = DEFAULT_ASSISTANT_LABELS.filter((r) => !existingLabels.has(r.label));
      actualRowData = [...actualRowData, ...missingDefaults];
      local.Value.value = actualRowData;
    }
  } else {
    actualRowData = DEFAULT_ASSISTANT_LABELS;
  }

  // Group the rows by group property
  const groups = ["Welcome", "Suggestions", "Mode", "Tone"];
  const groupedRows = groups.map((group) => ({
    group,
    rows: actualRowData.filter((row) => row.group === group),
  }));

  const handleValueChange = (idx: number, newValue: string) => {
    const updated = actualRowData.map((row, i) => (i === idx ? { ...row, value: newValue } : row));
    local.Value.value = updated;
    debouncedHandler(handlers["onChange"], new Event("onChange"), updated);
  };

  return (
    <Typography component="div" variant="body2" className="assistant-label-editor">
      <table className="assistant-label-editor-table">
        <colgroup>
          <col className="assistant-label-editor-label-col" />
          <col />
        </colgroup>
        <tbody>
          {groupedRows.map(({ group, rows }, groupIdx) => (
            <React.Fragment key={group}>
              <tr>
                <td colSpan={2} className="assistant-label-editor-group-header">
                  {group}
                </td>
              </tr>
              {rows.map((row, idx) => (
                <tr key={group + idx}>
                  <Tooltip title={row.label} placement="top">
                    <td className="assistant-label-editor-label-cell">{row.label}</td>
                  </Tooltip>
                  <td className="assistant-label-editor-value-cell">
                    <input
                      aria-label={row.label}
                      className="assistant-label-editor-input"
                      type="text"
                      value={row.value}
                      onChange={(e) =>
                        handleValueChange(
                          actualRowData.findIndex((r) => r.label === row.label),
                          e.target.value
                        )
                      }
                    />
                  </td>
                </tr>
              ))}
              {groupIdx < groupedRows.length - 1 && (
                <tr>
                  <td colSpan={2} className="assistant-label-editor-group-gap" />
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </Typography>
  );
}
